import React, {
  useEffect,
  Fragment,
  useState,
  useRef,
  useCallback,
} from 'react';
import {
  Table,
  Divider,
  Tooltip,
  Button,
  Typography,
  Dropdown,
  Menu,
  Popover,
  Input,
  Select,
  Checkbox,
  List,
  Modal,
  Spin,
} from 'antd';
import { Link } from 'react-router-dom';
import Icon, { EllipsisOutlined, ReloadOutlined } from '@ant-design/icons';
import styles from './queryhistory.module.sass';
import { STATUS } from '../../../../redux/constants';
import VersionCheck from '../../../../shared-components/VersionCheck';
import { ReactComponent as no_results } from '../../../../assets/icons/no_results.svg';
import { ColumnTitle } from '../../../../shared-components/ColumnTitle';
import PermissionDeniedPage from '../../../../shared-components/PermissionDeniedPage';
import { SearchOutlined } from '@ant-design/icons';
import { ReactComponent as copy_icon } from '../../../../assets/icons/Copy.svg';
import { ReactComponent as download_icon } from '../../../../assets/icons/Download.svg';
import {
  infoNotification,
  showNotification,
  successNotification,
} from '../../../../shared-components/Notifications';
import LoadingSilentPush from '../../../../shared-components/LoadingSilentPush';

import code_snippets_styles from '../../../../shared-components/CopyEndpoint/copy-endpoint.module.sass';
import { JSONViewer } from '../../../../shared-components/JSONViewer';
import { incrementUIRequestsCounter } from '../../../../redux/utils/websocket/incrementUIRequestsCounter';
import { useDispatch } from 'react-redux';

function QueryHistory(props) {
  const { Text, Title } = Typography;
  const NoDataIcon = (props) => <Icon component={no_results} {...props} />;
  const currentPage = useRef(1);
  const selectedFileUUID = useRef(null);
  const [tableFilter, setTableFilter] = useState({
    urlFilter: '',
    hoursFilter: '24',
    hoursFilterLabel: '24 hours',
    users: [],
    sorter: {},
    kick_search_off: false,
  });
  const CopyIcon = (props) => <Icon component={copy_icon} {...props} />;
  const DownloadIcon = (props) => <Icon component={download_icon} {...props} />;
  const isSelectAllclicked = useRef(false);
  const [copyModalVisible, setCopyModalVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const PrettyPrintJson = React.memo(({ data }) => <JSONViewer data={data} />);
  const dispatch = useDispatch();

  useEffect(() => {
    document.title = 'Query History - Silent Push';
    props.loadUser();
    props.loadMySubscriptionPlan();
    fetchData();
    return () => {
      selectedFileUUID.current = null;
      props.clearCopyHistoryQueryResult();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (props?.user?.is_organization_admin) {
      props.loadUsersInfo();
    }
  }, [props?.user]);

  useEffect(() => {
    if (tableFilter?.kick_search_off) {
      handleSearch();
    }
  }, [tableFilter]);

  useEffect(() => {
    if (props?.copyQueryHistoryResultStatus === STATUS.LOADED) {
      setCopyModalVisible(true);
    }
  }, [props?.copyQueryHistoryResultStatus]);

  const clearFilters = () => {
    if (props.historyQueriesStatus === STATUS.LOADED) {
      currentPage.current = 1;
      setTableFilter({
        urlFilter: '',
        hoursFilter: '24',
        hoursFilterLabel: '24 hours',
        kick_search_off: true,
        users: [],
      });
    }
  };

  const getSortingParams = () => {
    let sortingParams = null;
    if (tableFilter?.sorter && tableFilter?.sorter?.order) {
      sortingParams =
        (tableFilter?.sorter?.order === 'descend' ? '-' : '') +
        tableFilter?.sorter?.columnKey;
    }
    return sortingParams;
  };

  const getUsersFilter = () => {
    if (props.user?.is_organization_admin) {
      const all_users = tableFilter?.users;
      return all_users.toString();
    } else {
      return '';
    }
  };

  const handleTableChange = (pagination, filters, sort) => {
    setTableFilter({
      ...tableFilter,
      sorter: { columnKey: sort?.columnKey, order: sort?.order },
      kick_search_off: true,
    });
  };

  const handleSearch = () => {
    fetchData();
  };

  const fetchData = () => {
    const sortingParams = getSortingParams();
    const users = getUsersFilter();
    props.loadHistoryQueries(
      currentPage.current,
      tableFilter?.urlFilter,
      tableFilter?.hoursFilter,
      sortingParams,
      users,
    );
    incrementUIRequestsCounter();
  };

  const getViewQueryResult = (query) => {
    return `/explore-result?queryType=${query?.query_type}&type=${
      query?.type
    }&historyQueryUUID=${
      query?.uuid
    }&historyQueryCreatedAt=${query?.created?.substring(0, 19)} (UTC)&name=${
      query.origin_label
    }`;
  };

  const copyQueryResult = (query) => {
    selectedFileUUID.current = query?.uuid;
    props.copyHistoryQueryResult(query?.uuid);
    incrementUIRequestsCounter();
  };

  const downloadQueryResult = (query) => {
    selectedFileUUID.current = query?.uuid;
    props.downloadHistoryQueryResult(query?.uuid);
    incrementUIRequestsCounter();
  };

  const DropdownMenu = (record) => {
    return (
      <Menu className="ant-dropdown-menu">
        <Menu.Item
          style={{ height: '32px', marginTop: '0px', marginBottom: '8px' }}
          className="ant-dropdown-menu-item"
          onClick={() => {
            let text = getQueryFullURL(record?.record);
            navigator.clipboard.writeText(text).then(
              showNotification(
                infoNotification({
                  statusCode: 100,
                  message: 'Copied to clipboard!',
                }),
              ),
            );
          }}
        >
          Copy API Endpoint
        </Menu.Item>
      </Menu>
    );
  };

  const getQueryFullURL = (record) => {
    if (record?.url && record?.is_enabled) {
      const host = new URL(process.env.REACT_APP_API_URL);
      let full_url = host;
      if (record?.url.includes('explore-recipes/')) {
        full_url = host + 'api/v1/' + record?.url;
      } else {
        full_url = host + 'api/v1/merge-api/' + record?.url;
      }
      return full_url;
    }
    return 'Access Denied';
  };

  const getViewResultsTooltip = (record) => {
    if (!record?.job_id) {
      return 'The results for this query is not stored';
    } else if (!record?.is_visualizable) {
      return 'This query is not visualizable';
    } else if (record?.status !== 'SUCCESS') {
      return 'Results are not ready yet';
    }
    return 'View Results';
  };

  const getCopyResultsTooltip = (record) => {
    if (!record?.job_id) {
      return 'The results for this query is not stored';
    } else if (record?.status !== 'SUCCESS') {
      return 'Results are not ready yet';
    }
    return 'Copy Results';
  };

  const getDownloadResultsTooltip = (record) => {
    if (!record?.job_id) {
      return 'The results for this query is not stored';
    } else if (record?.status !== 'SUCCESS') {
      return 'Results are not ready yet';
    }
    return 'Download Results';
  };

  const isViewMonitorBtnDisabled = (record) => {
    return (
      !record?.is_visualizable ||
      !record?.job_id ||
      record?.status !== 'SUCCESS'
    );
  };

  const handleCheckBoxClick = (user) => {
    let users = tableFilter?.users;
    if (users.includes(user?.username)) {
      users = users.filter(function (username) {
        return username !== user?.username;
      });
      isSelectAllclicked.current = false;
    } else {
      users.push(user?.username);
    }
    if (props?.organisation_users_info?.length === users?.length) {
      isSelectAllclicked.current = true;
    }
    setTableFilter({
      ...tableFilter,
      users: users,
      kick_search_off: false,
    });
  };

  const handleSelectAll = () => {
    if (isSelectAllclicked.current) {
      isSelectAllclicked.current = false;
      setTableFilter({
        ...tableFilter,
        users: [],
      });
    } else {
      isSelectAllclicked.current = true;
      const all_users = props?.organisation_users_info?.map(
        (user) => user.username,
      );
      setTableFilter({
        ...tableFilter,
        users: all_users,
      });
    }
  };

  const handleFilterSubmit = () => {
    fetchData();
  };

  const handleFilterReset = () => {
    isSelectAllclicked.current = false;
    setTableFilter({
      ...tableFilter,
      users: [],
      kick_search_off: true,
    });
  };

  const refresh = () => {
    fetchData();
  };

  const getUsersFilterDropDown = (confirm, clearFilters) => {
    return (
      <div>
        <div style={{ padding: '10px 10px 5px 10px' }}>
          <Checkbox
            checked={isSelectAllclicked.current}
            style={{ color: '#b1c4d0' }}
            onClick={handleSelectAll}
          >
            Select all
          </Checkbox>
        </div>

        <Divider style={{ margin: 0, backgroundColor: '#fff' }} plain />
        <div style={{ padding: '5px 0px 10px 10px' }}>
          <List
            style={{ minWidth: '200px', height: '200px', overflow: 'auto' }}
            dataSource={props?.organisation_users_info}
            renderItem={(user, idx) => (
              <List.Item style={{ border: 0, padding: '5px 5px 5px 0' }}>
                <Checkbox
                  key={user.username}
                  onClick={() => handleCheckBoxClick(user)}
                  checked={tableFilter?.users.includes(user.username)}
                  style={{ color: '#b1c4d0' }}
                >
                  {user.email}
                </Checkbox>
              </List.Item>
            )}
          />
        </div>
        <Divider style={{ margin: 0, backgroundColor: '#fff' }} plain />
        <div style={{ padding: 8, minHeight: '40px' }}>
          <Button
            type="primary"
            onClick={() => {
              handleFilterSubmit();
              confirm();
            }}
            size="small"
            style={{ float: 'right' }}
          >
            Apply
          </Button>
          <Button
            onClick={() => {
              handleFilterReset();
              confirm();
            }}
            size="small"
            style={{ marginRight: '8px', border: 'none', float: 'right' }}
          >
            Clear
          </Button>
        </div>
      </div>
    );
  };

  const columns = [
    {
      title: <ColumnTitle columnName="Query Execution Date" />,
      dataIndex: 'created',
      key: 'created',
      align: 'left',
      render: (text, record) => (
        <span>{record?.created?.substring(0, 19)}</span>
      ),
      sorter: true,
      sortOrder:
        tableFilter?.sorter?.columnKey === 'created' &&
        tableFilter?.sorter?.order,
    },
    {
      title: <ColumnTitle columnName="Query Type" />,
      dataIndex: 'origin_label',
      key: 'origin_label',
      align: 'left',
      render: (text, record) => <span>{record?.origin_label}</span>,
      sorter: true,
      sortOrder:
        tableFilter?.sorter?.columnKey === 'origin_label' &&
        tableFilter?.sorter?.order,
    },
    {
      title: <ColumnTitle columnName="Executed By" />,
      dataIndex: 'executed_by',
      key: 'executed_by',
      align: 'left',
      render: (text, record) => <span>{record?.executed_by}</span>,
      sorter: false,
      hidden: props?.user?.is_organization_admin === false,
      sortOrder:
        tableFilter?.sorter?.columnKey === 'executed_by' &&
        tableFilter?.sorter?.order,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => getUsersFilterDropDown(confirm, clearFilters),
    },
    {
      title: <ColumnTitle columnName="Query URL" />,
      dataIndex: 'url',
      key: 'url',
      align: 'left',
      render: (text, record) => (
        <Popover content={getQueryFullURL(record)}>
          {record.is_enabled ? (
            text?.length > 50 ? (
              <Text>{text?.substring(0, 50)}...</Text>
            ) : (
              <Text>{text}</Text>
            )
          ) : (
            'Access Denied'
          )}
        </Popover>
      ),
      sorter: true,
      sortOrder:
        tableFilter?.sorter?.columnKey === 'url' && tableFilter?.sorter?.order,
    },
    {
      title: <ColumnTitle columnName="Total Results" />,
      dataIndex: 'total_results',
      key: 'total_results',
      align: 'left',
      render: (text, record) => (
        <span>
          {record?.total_results !== null ? record?.total_results : 'N/A'}
        </span>
      ),
      sorter: true,
      sortOrder:
        tableFilter?.sorter?.columnKey === 'total_results' &&
        tableFilter?.sorter?.order,
    },
    {
      title: <ColumnTitle columnName="Status" />,
      dataIndex: 'status',
      key: 'status',
      align: 'left',
      width: '120px',
      render: (text, record) => (
        <span>
          {record?.status === 'SUCCESS' ? 'COMPLETED' : record?.status}
        </span>
      ),
      sorter: true,
      sortOrder:
        tableFilter?.sorter?.columnKey === 'status' &&
        tableFilter?.sorter?.order,
    },
    {
      title: '',
      key: 'actions',
      width: '80px',
      render: (record) => (
        <div className={styles.action_buttons}>
          <Tooltip placement="top" title={getViewResultsTooltip(record)}>
            <Button
              type="link"
              disabled={isViewMonitorBtnDisabled(record)}
              data-cy="queryHistoryViewButton"
            >
              <Link
                to={getViewQueryResult(record)}
                target="_blank"
                style={{
                  color: isViewMonitorBtnDisabled(record)
                    ? '#595959'
                    : '#3C9AE8',
                }}
              >
                View
              </Link>
            </Button>
          </Tooltip>

          <Tooltip title={getCopyResultsTooltip(record)}>
            <Button
              data-cy="queryHistoryCopyButton"
              disabled={!record?.job_id || record?.status !== 'SUCCESS'}
              icon={<CopyIcon />}
              style={{
                background:
                  'rgba(255, 255, 255, 0.08) none repeat scroll 0% 0%',
                border: 'medium none',
                borderRadius: 4,
                display: 'inline',
                color: 'white',
                marginLeft: 5,
              }}
              loading={
                props?.copyQueryHistoryResultStatus === STATUS.LOADING &&
                selectedFileUUID.current == record?.uuid
              }
              onClick={() => copyQueryResult(record)}
            ></Button>
          </Tooltip>

          <Tooltip title={getDownloadResultsTooltip(record)}>
            <Button
              data-cy="queryHistoryDownloadButton"
              disabled={!record?.job_id || record?.status !== 'SUCCESS'}
              icon={<DownloadIcon />}
              style={{
                background:
                  'rgba(255, 255, 255, 0.08) none repeat scroll 0% 0%',
                border: 'medium none',
                borderRadius: 4,
                display: 'inline',
                color: 'white',
                marginLeft: 5,
              }}
              loading={
                props?.downloadQueryHistoryResultStatus === STATUS.LOADING &&
                selectedFileUUID.current == record?.uuid
              }
              onClick={() => downloadQueryResult(record)}
            ></Button>
          </Tooltip>

          <Dropdown
            data-cy="queryHistoryDotsButton"
            className={styles.dots}
            trigger={['click']}
            overlay={<DropdownMenu record={record} />}
          >
            <EllipsisOutlined />
          </Dropdown>
        </div>
      ),
    },
  ];

  return props.mySubscriptionPlanStatus === STATUS.LOADED ? (
    props.mySubscriptionPlan?.has_access_to_query_history ? (
      <div className={styles.queryhistory}>
        <Fragment>
          <div>
            <div style={{ marginTop: '15px' }}>
              <div className={styles.tableHeader}>
                <label className={styles.queriesTotal}>
                  {props.historyQueriesCount} queries in past
                </label>
                <Select
                  className={styles.dateFilter}
                  disabled={props.historyQueriesStatus === STATUS.LOADING}
                  onChange={(_, event) => {
                    setTableFilter({
                      ...tableFilter,
                      hoursFilter: event.value,
                      hoursFilterLabel: event.label,
                      kick_search_off: true,
                    });
                  }}
                  options={[
                    { label: '24 hours', value: '24' },
                    { label: '48 hours', value: '48' },
                    { label: '7 days', value: '168' },
                    { label: '30 days', value: '720' },
                  ]}
                  value={tableFilter?.hoursFilter}
                  defaultValue={tableFilter?.hoursFilter}
                />
              </div>
              <div className={styles?.searchContainer}>
                <Input
                  id="searchQueryHistory"
                  name="searchQueryHistory"
                  type="text"
                  value={tableFilter?.urlFilter}
                  className={styles?.searchBox}
                  onPressEnter={(e) => {
                    currentPage.current = 1;
                    handleSearch();
                  }}
                  onChange={(e) => {
                    if (e.target.value === '') {
                      currentPage.current = 1;
                      setTableFilter({
                        ...tableFilter,
                        urlFilter: e.target.value,
                        kick_search_off: true,
                      });
                    } else {
                      setTableFilter({
                        ...tableFilter,
                        urlFilter: e.target.value,
                        kick_search_off: false,
                      });
                    }
                  }}
                  allowClear
                  size="default"
                  placeholder="Search Query URL"
                  prefix={
                    <SearchOutlined
                      style={{ color: 'rgba(255, 255, 255, 0.3)' }}
                    />
                  }
                />

                <Divider className={styles?.filterDivider} type="vertical" />
                <Tooltip title="Clear all active filters">
                  <Button
                    className={styles?.clearFilters}
                    size="small"
                    onClick={clearFilters}
                  >
                    Clear Filters
                  </Button>
                </Tooltip>

                <Tooltip title="Refresh">
                  <Button
                    loading={props.historyQueriesStatus === STATUS.LOADING}
                    icon={<ReloadOutlined />}
                    className={styles?.refresh}
                    size="small"
                    onClick={refresh}
                  >
                    Refresh
                  </Button>
                </Tooltip>
              </div>
            </div>
          </div>

          <Table
            showSorterTooltip={false}
            columns={columns.filter((item) => !item.hidden)}
            dataSource={props?.historyQueries}
            pagination={{
              total: props?.historyQueriesCount,
              current: currentPage.current,
              showSizeChanger: false,
              pageSize: 50,
              onChange: (current, pageSize) => {
                currentPage.current = current;
                handleSearch();
                window.scrollTo(0, 0);
              },
              position: ['bottomRight'],
            }}
            onChange={handleTableChange}
            loading={props.historyQueriesStatus === STATUS.LOADING}
            locale={{
              emptyText: (
                <div className={styles.empty_table}>
                  <NoDataIcon style={{ fontSize: 32 }} />
                  <Title level={4}>Query History page is empty</Title>
                  <Text>
                    You didn't perform any query yet during the last{' '}
                    {tableFilter.hoursFilterLabel}
                  </Text>
                </div>
              ),
            }}
            rowKey={(record) => `${record.uuid}`}
          />
        </Fragment>

        <Modal
          title={'Copy Query History Results'}
          open={copyModalVisible}
          onCancel={() => setCopyModalVisible(false)}
          okText={'Copy'}
          confirmLoading={confirmLoading}
          onOk={() => {
            setConfirmLoading(true);
            const json_result = JSON.stringify(props?.queryHistoryResults);
            /**
             * Safari doesn't allow you programmatically interact with the clipboard
             * @see: https://developer.apple.com/forums/thread/691873
             */
            navigator.clipboard
              .writeText(json_result)
              .then(() => {
                showNotification(
                  successNotification({
                    statusCode: 100,
                    message: 'Copied to clipboard!',
                  }),
                );
              })
              .then(() => {
                setConfirmLoading(false);
                setCopyModalVisible(false);
              });
          }}
        >
          <pre className={code_snippets_styles.code_snippet}>
            {props?.copyQueryHistoryResultStatus === STATUS.LOADED ? (
              <PrettyPrintJson data={props?.queryHistoryResults} />
            ) : (
              <Spin />
            )}
          </pre>
        </Modal>

        <VersionCheck parentComponentStatus={props.historyQueriesStatus} />
      </div>
    ) : (
      <PermissionDeniedPage />
    )
  ) : (
    <LoadingSilentPush size={80} positionCenter />
  );
}

export default QueryHistory;
